import React from "react";
import { Link } from "react-router-dom";
import { Col, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Helpers from "./Helpers";
import Loader from "./loader";
import Profile from "../assets/images/couple-default.png";
import companyLogo from "../assets/images/logo.png";
import { resolveRetreatImageUrl } from "../utils/processRetreatImage";
import {
  formatCoupleName,
  formatRetreatDateRange,
  formatRetreatLanguage,
  getParishAddressLines,
  getRetreatDayName,
  getRetreatParishName,
  normalizeExternalLink,
  parseRetreatContactInfo,
} from "../utils/retreatPublicDisplay";

const ContactPhones = ({ phones }) => {
  if (!phones?.length) return null;
  return (
    <div className="rr-navy-contact-phones">
      {phones.map((phone, idx) =>
        phone.name ? (
          <React.Fragment key={`ph-${idx}`}>
            <span className="rr-navy-contact-name">{phone.name}</span>
            <span className="rr-navy-contact-number">- {phone.number}</span>
          </React.Fragment>
        ) : (
          <span
            key={`ph-${idx}`}
            className="rr-navy-contact-number rr-navy-contact-number-only"
          >
            {phone.number}
          </span>
        ),
      )}
    </div>
  );
};

const QuestionsCard = ({ emails, phones, title }) => (
  <div className="rr-navy-questions-card h-100">
    <div className="rr-navy-questions-header">
      <i className="mdi mdi-email-outline rr-navy-mail-icon"></i>
      <h4 className="rr-navy-questions-title">{title}</h4>
    </div>
    <div className="rr-navy-contact-detail">
      {emails.map((email, idx) => (
        <div key={`em-${idx}`} className="rr-navy-contact-email">
          {email}
        </div>
      ))}
      <ContactPhones phones={phones} />
    </div>
  </div>
);

const LeaderCard = ({
  couple,
  label,
  badgeClass,
  columnClass,
  loading,
  imageSrc,
  actions,
}) => {
  if (!couple) return null;
  return (
    <Col lg={4} md={4} sm={6} xs={12} className={columnClass}>
      <div className="rr-leadership-card">
        {actions ? <div className="rr-leader-actions">{actions}</div> : null}
        {loading ? (
          <div className="text-center image-set-retreat py-4">
            <Loader />
          </div>
        ) : (
          <img
            src={imageSrc}
            alt={label}
            className="rr-leader-photo coupleImage"
          />
        )}
        <div className="rr-leader-name">{formatCoupleName(couple)}</div>
        <div className={`rr-leader-role-badge ${badgeClass}`}>{label}</div>
      </div>
    </Col>
  );
};

const RetreatPublicView = ({
  retreat,
  roles,
  showChrome = false,
  heroLoading = false,
  resolveHeroImage,
  onHeroImageError,
  resolveCoupleImage,
  leaderLoading = {},
  renderLeaderActions,
}) => {
  const { t } = useTranslation();

  if (!retreat) return null;

  const parishTitle = getRetreatParishName(retreat);
  const languageLabel = formatRetreatLanguage(retreat.language);
  const dateLabel = formatRetreatDateRange(retreat);
  const rawHero = retreat.image1 || retreat.image2;
  const heroImage = rawHero
    ? resolveHeroImage
      ? resolveHeroImage(rawHero)
      : resolveRetreatImageUrl(rawHero)
    : "";
  const locationLines = retreat.location
    ? String(retreat.location)
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
    : getParishAddressLines(retreat.myparish);
  const hasLeadership = Boolean(
    roles && (roles.leadCouple || roles.coLeadCouple || roles.mentor),
  );
  const weekendDayCount =
    retreat.dateFrom && retreat.dateTo
      ? Math.max(
          1,
          moment(retreat.dateTo).diff(moment(retreat.dateFrom), "days") + 1,
        )
      : retreat.schedule?.length || 0;
  const contactInfo = parseRetreatContactInfo(retreat.description || "");
  const hasContactInfo =
    contactInfo.emails.length > 0 || contactInfo.phones.length > 0;
  const parishRecord = retreat.myparish;
  const parishCardName =
    (retreat.language === "spanish" && parishRecord?.spanishParish) ||
    parishRecord?.parish ||
    retreat.parishName ||
    "";
  const parishImage = resolveRetreatImageUrl(
    parishRecord?.imageUrl || retreat.parishImageUrl,
  );
  const parishHref = normalizeExternalLink(
    parishRecord?.link || retreat.parishLink,
  );
  const parishAddressLines = getParishAddressLines(parishRecord);
  const showParishCard = Boolean(
    parishCardName || parishImage || parishAddressLines.length,
  );
  const schedule = Array.isArray(retreat.schedule) ? retreat.schedule : [];

  const coupleImage = (couple) => {
    if (!couple?.imageUrl) return Profile;
    if (resolveCoupleImage) return resolveCoupleImage(couple.imageUrl) || Profile;
    return resolveRetreatImageUrl(couple.imageUrl) || Profile;
  };

  const descriptionBlock = retreat.description ? (
    <Row className="g-4 align-items-stretch">
      <Col lg={hasContactInfo ? 8 : 12} md={hasContactInfo ? 7 : 12} xs={12}>
        <div className="rr-cost-info-box h-100">
          <div
            dangerouslySetInnerHTML={{
              __html: retreat.description,
            }}
          />
        </div>
      </Col>
      {hasContactInfo ? (
        <Col lg={4} md={5} xs={12}>
          <QuestionsCard
            emails={contactInfo.emails}
            phones={contactInfo.phones}
            title={t("questions")}
          />
        </Col>
      ) : null}
    </Row>
  ) : null;

  const body = (
    <div className="w-100">
      <Row className="w-100 mx-0 align-items-center mb-4">
        <Col
          lg={heroImage || heroLoading ? 7 : 12}
          md={heroImage || heroLoading ? 7 : 12}
          xs={12}
          className="pe-lg-4 text-start"
        >
          <div className="rr-upcoming-badge">
            <i className="mdi mdi-calendar-outline me-1"></i>
            {t("upcoming_retreat")}
          </div>

          <h1 className="rr-hero-parish-title">
            {parishTitle}
            {languageLabel ? ` (${languageLabel})` : null}
          </h1>

          {parishTitle ? (
            <h3 className="rr-hero-parish-subtitle">{parishTitle}</h3>
          ) : null}

          {dateLabel ? (
            <div className="rr-hero-meta-item">
              <i className="mdi mdi-calendar-month-outline rr-hero-meta-icon"></i>
              <div className="rr-hero-meta-text">{dateLabel}</div>
            </div>
          ) : null}

          {locationLines.length ? (
            <div className="rr-hero-meta-item">
              <i className="mdi mdi-map-marker-outline rr-hero-meta-icon"></i>
              <div className="rr-hero-meta-text">
                {locationLines.map((line, i) => (
                  <div key={`loc-${i}`}>{line}</div>
                ))}
              </div>
            </div>
          ) : null}
        </Col>

        {heroLoading || heroImage ? (
          <Col lg={5} md={5} xs={12} className="mt-4 mt-md-0">
            <div className="rr-church-image-card">
              {heroLoading ? (
                <div className="text-center py-5">
                  <Loader />
                </div>
              ) : (
                <img
                  src={heroImage}
                  alt={parishTitle || "Retreat"}
                  onError={onHeroImageError}
                />
              )}
            </div>
          </Col>
        ) : null}
      </Row>

      {retreat.heading ? (
        <div className="rr-theme-box w-100 my-3">
          <div className="rr-theme-label">{t("retreat_theme")}</div>
          <h2>
            <i>{retreat.heading}</i>
          </h2>
        </div>
      ) : null}

      {retreat.mission_statement ? (
        <div className="rr-mission-banner-card w-100">
          <Row className="align-items-center">
            <Col lg={2} md={3} xs={12} className="rr-mission-left mb-3 mb-md-0">
              <i className="mdi mdi-heart-outline rr-mission-heart-icon"></i>
              <div className="rr-mission-title-group">
                <p>{t("our_mission")}</p>
              </div>
            </Col>
            <Col lg={10} md={9} xs={12} className="d-flex align-items-center">
              <div className="rr-mission-divider d-none d-md-block"></div>
              <div className="rr-mission-right-quote">
                <div
                  dangerouslySetInnerHTML={{
                    __html: retreat.mission_statement,
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      ) : null}

      {hasLeadership ? (
        <div className="rr-leadership-section w-100">
          <div className="rr-leadership-heading">
            <div className="rr-leadership-heading-line"></div>
            <span className="rr-leadership-heart-icon">♥</span>
            <span>{t("retreat_leadership")}</span>
            <span className="rr-leadership-heart-icon">♥</span>
            <div className="rr-leadership-heading-line"></div>
          </div>

          <Row className="justify-content-center g-4">
            <LeaderCard
              couple={roles?.leadCouple}
              label={t("lead_couple")}
              badgeClass="rr-role-badge-lead"
              loading={leaderLoading.leadCouple}
              imageSrc={coupleImage(roles?.leadCouple)}
              actions={
                renderLeaderActions
                  ? renderLeaderActions(roles?.leadCouple)
                  : null
              }
            />
            <LeaderCard
              couple={roles?.coLeadCouple}
              label={t("co_lead_couple")}
              badgeClass="rr-role-badge-colead"
              columnClass="leadCoCouples"
              loading={leaderLoading.coLeadCouple}
              imageSrc={coupleImage(roles?.coLeadCouple)}
              actions={
                renderLeaderActions
                  ? renderLeaderActions(roles?.coLeadCouple)
                  : null
              }
            />
            <LeaderCard
              couple={roles?.mentor}
              label={t("mentor_couple")}
              badgeClass="rr-role-badge-mentor"
              columnClass="mentorCouples"
              loading={leaderLoading.mentor}
              imageSrc={coupleImage(roles?.mentor)}
              actions={
                renderLeaderActions ? renderLeaderActions(roles?.mentor) : null
              }
            />
          </Row>
        </div>
      ) : null}

      {schedule.length ? (
        <div className="rr-schedule-container-card w-100">
          <div className="rr-schedule-header-title">
            <i className="mdi mdi-calendar-clock rr-schedule-header-icon"></i>
            <span>{t("retreat_schedule")}</span>
          </div>

          <Row className="g-4 mb-4 align-items-stretch">
            <Col lg={8} md={7} xs={12}>
              <div className="table-responsive rounded-3 overflow-hidden">
                <Table className="rr-schedule-table-styled mb-0">
                  <thead>
                    <tr>
                      <th>{t("day")}</th>
                      <th>{t("date")}</th>
                      <th>{t("start")}</th>
                      <th>{t("end")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((item, i) => (
                      <tr key={i}>
                        <td>{getRetreatDayName(item.date, retreat.language)}</td>
                        <td>
                          {Helpers.convertInWordTimming(
                            item.date,
                            "MM-DD-YYYY",
                            retreat.language === "spanish" ? "es" : "en",
                          )}
                        </td>
                        <td>{item.from}</td>
                        <td>{item.to}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col lg={4} md={5} xs={12}>
              <div className="rr-schedule-right-pink-card">
                <i className="mdi mdi-account-heart-outline rr-schedule-right-icon"></i>
                <p className="rr-schedule-right-text">
                  {t("all_couples_expected_msg", { count: weekendDayCount })}
                </p>
              </div>
            </Col>
          </Row>

          {descriptionBlock}
        </div>
      ) : retreat.description ? (
        <div className="rr-schedule-container-card w-100">{descriptionBlock}</div>
      ) : null}

      {showParishCard ? (
        <div className="rr-parish-card w-100">
          <div className="rr-parish-accent"></div>
          <Row className="align-items-center g-4">
            {parishImage ? (
              <Col lg={3} md={4} xs={12}>
                <div className="rr-parish-logo-tile">
                  {parishHref ? (
                    <a href={parishHref} target="_blank" rel="noopener noreferrer">
                      <img
                        src={parishImage}
                        alt={parishCardName || "Parish"}
                        className="rr-parish-logo"
                      />
                    </a>
                  ) : (
                    <img
                      src={parishImage}
                      alt={parishCardName || "Parish"}
                      className="rr-parish-logo"
                    />
                  )}
                </div>
              </Col>
            ) : null}
            <Col lg={parishImage ? 9 : 12} md={parishImage ? 8 : 12} xs={12}>
              <div className="rr-parish-eyebrow">
                <i className="mdi mdi-church"></i>
                {t("hosting_parish")}
              </div>
              {parishCardName ? (
                <h3 className="rr-parish-name">{parishCardName}</h3>
              ) : null}
              {parishAddressLines.length ? (
                <div className="rr-parish-address">
                  <span className="rr-parish-address-badge">
                    <i className="mdi mdi-map-marker-outline"></i>
                  </span>
                  <div className="rr-parish-address-lines">
                    {parishAddressLines.map((line, i) => (
                      <div key={`pa-${i}`}>{line}</div>
                    ))}
                  </div>
                </div>
              ) : null}
              {parishHref ? (
                <div className="rr-parish-actions">
                  <a
                    className="rr-parish-btn rr-parish-btn-ghost"
                    href={parishHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="mdi mdi-open-in-new"></i>
                    {t("visit_parish_website")}
                  </a>
                </div>
              ) : null}
            </Col>
          </Row>
        </div>
      ) : null}
    </div>
  );

  if (!showChrome) {
    return <div className="rr-public-view w-100">{body}</div>;
  }

  return (
    <div className="rr-public-view">
      <div className="rr-top-header w-100 mb-3">
        <div className="compLogo rr-logo-container p-0">
          <img src={companyLogo} alt="logo" className="logo rr-top-logo" />
        </div>
        <Link
          to="/create-ticket"
          state={{ pageName: "registration-retreat" }}
          target="_blank"
          rel="noopener noreferrer"
          className="rr-need-help-link"
        >
          <span>{t("need_help")}</span>
          <span className="rr-help-question-icon">?</span>
        </Link>
      </div>
      <div className="auth-form-light main-register rr-glass-card py-4 px-3 px-sm-4 mt-2 text-start">
        {body}
      </div>
    </div>
  );
};

export default RetreatPublicView;
